import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Section } from "@/components/Primitives";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkle, CheckSquare, Square } from "@phosphor-icons/react";

const CLASS_GROUPS = ["Class 9", "Class 10", "Class 11", "Class 12", "B.Com", "B.Sc", "B.CA"];
const CUSTOM_SECTION_VALUE = "__custom__";

function normSec(s) {
  return (s || "").trim().toUpperCase();
}

export default function TeacherTestBuilder() {
  const [subjects, setSubjects] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [sectionsForClass, setSectionsForClass] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [form, setForm] = useState({
    title: "", subject_id: "", type: "mcq", class_group: "Class 10",
    duration_minutes: 30, chapter_ids: [], question_ids: [],
    section: "",
  });
  const [loadingAi, setLoadingAi] = useState(false);
  const nav = useNavigate();

  useEffect(() => { api.get("/subjects").then(r => setSubjects(r.data)); }, []);

  useEffect(() => {
    let cancelled = false;
    setSectionsLoading(true);
    api
      .get("/users/sections-in-class", { params: { class_group: form.class_group } })
      .then((r) => {
        if (!cancelled) setSectionsForClass(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => {
        if (!cancelled) setSectionsForClass([]);
      })
      .finally(() => {
        if (!cancelled) setSectionsLoading(false);
      });
    return () => { cancelled = true; };
  }, [form.class_group]);

  const subject = subjects.find(s => s.id === form.subject_id);

  useEffect(() => {
    if (!form.subject_id) return;
    const p = new URLSearchParams();
    p.set("subject_id", form.subject_id);
    p.set("type", form.type);
    api.get(`/questions?${p.toString()}`).then(r => setQuestions(r.data));
  }, [form.subject_id, form.type]);

  const filteredQs = useMemo(() => {
    if (form.chapter_ids.length === 0) return questions;
    return questions.filter(q => form.chapter_ids.includes(q.chapter_id));
  }, [questions, form.chapter_ids]);

  const toggle = (arr, v) => arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];

  const sectionMatch = form.section?.trim()
    ? sectionsForClass.find((s) => normSec(s) === normSec(form.section))
    : null;
  const sectionSelectValue =
    !form.section?.trim()
      ? ""
      : sectionMatch !== undefined && sectionMatch != null
        ? sectionMatch
        : CUSTOM_SECTION_VALUE;

  const save = async () => {
    if (!form.title || !form.subject_id || form.question_ids.length === 0) {
      toast.error("Add title, subject and at least one question"); return;
    }
    try {
      await api.post("/tests", form);
      toast.success("Test saved as draft");
      nav("/teacher/tests");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const aiGenIntoTest = async () => {
    if (!form.subject_id || form.chapter_ids.length === 0) { toast.error("Select subject and at least one chapter"); return; }
    setLoadingAi(true);
    try {
      const allNew = [];
      for (const cid of form.chapter_ids) {
        const { data } = await api.post("/questions/ai-generate", {
          subject_id: form.subject_id, chapter_id: cid, type: form.type,
          count: 3, difficulty: "medium", auto_save: true,
        });
        allNew.push(...data.questions);
      }
      // Refresh
      const p = new URLSearchParams();
      p.set("subject_id", form.subject_id);
      p.set("type", form.type);
      const { data: allQs } = await api.get(`/questions?${p.toString()}`);
      setQuestions(allQs);
      const newIds = allNew.map(q => q.id);
      setForm(f => ({ ...f, question_ids: Array.from(new Set([...f.question_ids, ...newIds])) }));
      toast.success(`AI added ${allNew.length} questions to test`);
    } catch (e) { toast.error("AI generation failed"); }
    finally { setLoadingAi(false); }
  };

  return (
    <div>
      <PageHeader eyebrow="Composer" title="New test paper" subtitle="Half-yearly, annual, chapter test or mixed — manual or AI-assisted." />

      <Section title="Paper details">
        <div className="card-academic p-6 grid md:grid-cols-2 gap-4">
          <div>
            <label className="overline block mb-2">Title</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="t-title"
              className="w-full bg-ivory border border-stone px-4 py-3" placeholder="e.g., Mathematics – Half Yearly 2026" />
          </div>
          <Select label="Class / Stream" value={form.class_group} onChange={v => setForm({
            ...form,
            class_group: v,
            subject_id: "",
            chapter_ids: [],
            question_ids: [],
            section: "",
          })}
            options={CLASS_GROUPS.map(c => ({ value: c, label: c }))} testid="t-class" />
          <Select label="Subject" value={form.subject_id} onChange={v => setForm({ ...form, subject_id: v, chapter_ids: [], question_ids: [] })}
            options={[{ value: "", label: "— Select —" }, ...subjects.filter(s => s.class_group === form.class_group).map(s => ({ value: s.id, label: s.name }))]} testid="t-subject" />
          <Select label="Type" value={form.type} onChange={v => setForm({ ...form, type: v, question_ids: [] })}
            options={[{ value: "mcq", label: "MCQ" }, { value: "descriptive", label: "Descriptive" }]} testid="t-type" />
          <div>
            <label className="overline block mb-2">Section</label>
            <select
              value={sectionSelectValue}
              disabled={sectionsLoading}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({
                  ...f,
                  section: v === CUSTOM_SECTION_VALUE ? "" : v,
                }));
              }}
              data-testid="t-section"
              className="w-full bg-ivory border border-stone px-4 py-3 disabled:opacity-60"
            >
              <option value="">All sections</option>
              {sectionsForClass.map((s) => (
                <option key={s} value={s}>
                  Section {s}
                </option>
              ))}
              <option value={CUSTOM_SECTION_VALUE}>Custom</option>
            </select>
            {!sectionsLoading && sectionsForClass.length === 0 && (
              <p className="mt-1 text-xs text-mossy">No students with a section in this class yet — use All or Custom.</p>
            )}
            {sectionSelectValue === CUSTOM_SECTION_VALUE && (
              <input
                value={form.section}
                onChange={(e) => setForm((f) => ({ ...f, section: e.target.value }))}
                placeholder="Enter section label"
                data-testid="t-section-custom"
                className="mt-2 w-full bg-ivory border border-stone px-4 py-3"
              />
            )}
          </div>
          <div>
            <label className="overline block mb-2">Duration (min)</label>
            <input type="number" min={5} value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: Number(e.target.value) })} data-testid="t-duration"
              className="w-full bg-ivory border border-stone px-4 py-3" />
          </div>
          <div className="md:col-span-2">
            <label className="overline block mb-2">Selected questions</label>
            <div className="font-mono text-2xl text-forest">{form.question_ids.length}</div>
          </div>
        </div>
      </Section>

      {subject && (
        <Section title="Chapters" right={<span className="overline">Mix and match</span>}>
          <div className="flex flex-wrap gap-2">
            {subject.chapters.map(c => {
              const active = form.chapter_ids.includes(c.id);
              return (
                <button key={c.id} onClick={() => setForm({ ...form, chapter_ids: toggle(form.chapter_ids, c.id) })}
                  data-testid={`chap-${c.id}`}
                  className={`px-3 py-1.5 text-sm border transition-colors ${active ? "bg-forest text-ivory border-forest" : "bg-ivory border-stone text-ink hover:border-forest"}`}>
                  {c.name}
                </button>
              );
            })}
          </div>
        </Section>
      )}

      {form.subject_id && (
        <Section title={`Available ${form.type.toUpperCase()} questions (${filteredQs.length})`}
          right={<button onClick={aiGenIntoTest} disabled={loadingAi} className="btn-secondary flex items-center gap-2" data-testid="ai-to-test">
            <Sparkle size={14} weight={loadingAi ? "fill" : "regular"} /> {loadingAi ? "Generating…" : "Generate with AI"}
          </button>}>
          {filteredQs.length === 0 ? (
            <div className="card-academic p-6 text-sm text-mossy">
              No questions yet for this selection. Use AI to generate some, or add manually in Question Bank.
            </div>
          ) : (
            <div className="space-y-2">
              {filteredQs.map(q => {
                const selected = form.question_ids.includes(q.id);
                return (
                  <button key={q.id} onClick={() => setForm({ ...form, question_ids: toggle(form.question_ids, q.id) })}
                    data-testid={`pick-${q.id}`}
                    className={`w-full text-left card-academic p-4 flex items-start gap-3 ${selected ? "border-forest bg-sand/40" : ""}`}>
                    {selected ? <CheckSquare size={20} className="text-forest mt-0.5" weight="duotone" /> : <Square size={20} className="text-mossy mt-0.5" />}
                    <div className="flex-1">
                      <div className="flex gap-2 mb-1">
                        <span className="overline">{q.difficulty}</span>
                        <span className="overline font-mono">{q.marks} marks</span>
                        {q.ai_generated && <span className="overline flex items-center gap-1 text-terracotta"><Sparkle size={10} /> AI</span>}
                      </div>
                      <div className="text-ink">{q.text}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Section>
      )}

      <div className="sticky bottom-0 bg-bone/90 backdrop-blur-sm border-t border-stone -mx-8 px-8 py-4 flex justify-end gap-3">
        <button onClick={() => nav("/teacher/tests")} className="btn-ghost" data-testid="cancel-test">Cancel</button>
        <button onClick={save} className="btn-primary" data-testid="save-test">Save as draft</button>
      </div>
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="overline block mb-2">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-ivory border border-stone px-4 py-3">
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
